/**
 * QuakeSkillEffect — Earth tree foundation evolution (stomp_evolved).
 *
 * Replaces Stomp entirely when unlocked. All values are baked — Quake does
 * NOT query Stomp upgrade keys (stunRadiusTiles delta, stunDurationMs delta).
 *
 * On activation:
 * - Breaks Hide
 * - Applies 12 damage to every enemy within 2.5 tiles via CombatHitResolver
 * - Applies stun for 1000ms to every enemy in radius via StatusEffectSystem
 * - Starts fast cooldown (400ms)
 *
 * The hotbar slot replacement (Stomp → Quake) is driven by `replacesId: "stomp"`
 * in earth.json and handled by SkillActivationComponent — not by this class.
 *
 * Extends InstantSkillEffect with cooldown management (same pattern as
 * StompSkillEffect — overrides `get cooldown()` and `update()`).
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see earth-spec.md § Nodes Array — Quake
 */

import { InstantSkillEffect } from '../../InstantSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface QuakeDeps {
  readonly statusEffects: {
    apply(effectId: string, ...args: unknown[]): void;
  };
  /**
   * Returns enemy entity IDs within the given pixel radius of the player.
   * Provided by the game layer.
   */
  readonly targeting: {
    getEnemiesInRadius(radiusPx: number): string[];
  };
  readonly player: {
    breakHide(): void;
  };
  /**
   * Damage pipeline — called once per enemy hit with the baked 12 damage.
   * Resolves through the normal CombatHitResolver pipeline.
   */
  readonly combat: {
    applyDamage(targetId: string, damage: number, opts: { sourceSkillId: string }): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface QuakeConfig {
  /** Fast cooldown tier — 400ms. */
  readonly cooldownMs: number;
  /** Baked stun duration — not inherited from Stomp upgrades. */
  readonly bakedStunDurationMs: number;
  /** Baked radius in tiles — not inherited from Stomp upgrades. */
  readonly bakedRadiusTiles: number;
  /** Baked damage applied to each enemy in radius. */
  readonly bakedDamage: number;
  /** World units per tile — converts baked radius to pixels. */
  readonly tileSizePx: number;
}

const DEFAULT_CONFIG: QuakeConfig = {
  cooldownMs: 400,            // fast cooldown tier
  bakedStunDurationMs: 1000,  // same as Stomp base, not inherited from upgrades
  bakedRadiusTiles: 2.5,      // expanded from Stomp's 1.5-tile base
  bakedDamage: 12,            // AoE damage — Stomp dealt zero
  tileSizePx: 32,             // TILE_SIZE from Constants
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class QuakeSkillEffect extends InstantSkillEffect {
  readonly skillId = 'stomp_evolved';

  private readonly deps: QuakeDeps;
  private readonly config: QuakeConfig;

  // Cooldown state
  private _cooldownRemaining = 0;

  // Per-activation telemetry
  private _lastRadiusPx = 0;
  private _lastStunCount = 0;
  private _lastDamageDealt = 0;
  private _activationCount = 0;
  private _hideBreakCount = 0;

  constructor(deps: QuakeDeps, config: Partial<QuakeConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get lastRadiusPx(): number { return this._lastRadiusPx; }
  get lastStunCount(): number { return this._lastStunCount; }
  get lastDamageDealt(): number { return this._lastDamageDealt; }
  get activationCount(): number { return this._activationCount; }
  get hideBreakCount(): number { return this._hideBreakCount; }

  // --- InstantSkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Break Hide on activation
    this.deps.player.breakHide();
    this._hideBreakCount++;

    // Baked radius — no resolver queries
    const radiusPx = this.config.bakedRadiusTiles * this.config.tileSizePx;
    const targets = this.deps.targeting.getEnemiesInRadius(radiusPx);

    let damageDealt = 0;
    for (const targetId of targets) {
      // Damage through CombatHitResolver pipeline
      this.deps.combat.applyDamage(targetId, this.config.bakedDamage, {
        sourceSkillId: 'stomp_evolved',
      });
      damageDealt += this.config.bakedDamage;

      // Stun via StatusEffectSystem — baked 1000ms, sourceSkillId: 'stomp_evolved'
      this.deps.statusEffects.apply('stun', this.config.bakedStunDurationMs, {
        sourceSkillId: 'stomp_evolved',
        targetId,
      });
    }

    this._lastRadiusPx = radiusPx;
    this._lastStunCount = targets.length;
    this._lastDamageDealt = damageDealt;
    this._activationCount++;

    // Start fast cooldown
    this._cooldownRemaining = this.config.cooldownMs;

    return true;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
